import React from 'react';
import { useParams, Navigate, useLocation } from 'react-router-dom';
import { Box, Alert, Typography } from '@mui/material';
import { useAuth } from 'contexts/AuthContext';
import { getRuntimeConfig } from 'runtime/runtimeConfig';
import PortalMovedNotice from './PortalMovedNotice';

interface TenantAccessGuardProps {
  children: React.ReactNode;
}

/**
 * TenantAccessGuard ensures that tenant_users can only access:
 * 1. Routes they have tenant_roles for
 * 2. Non-admin routes (redirects away from admin routes)
 */
const TenantAccessGuard: React.FC<TenantAccessGuardProps> = ({ children }) => {
  const { user: authUser } = useAuth();
  const { tenantId } = useParams<{ tenantId: string }>();
  const location = useLocation();
  
  // end_users and supervisors are portal-only roles. `/portal` itself redirects
  // to the first enabled tab (PortalLayout), so no capability is assumed here.
  // On the control plane there is no portal any more (App.tsx sends /portal
  // back here): show where it moved instead of redirecting in a loop.
  if (authUser?.role === 'end_user' || authUser?.role === 'supervisor') {
    if (getRuntimeConfig().mode === 'control_plane') {
      return <PortalMovedNotice />;
    }
    return <Navigate to="/portal" replace />;
  }

  // Admins have access to everything
  if (authUser?.role === 'admin') {
    return <>{children}</>;
  }
  
  // If not a tenant_user, allow access (might be undefined during loading)
  if (authUser?.role !== 'tenant_user') {
    return <>{children}</>;
  }
  
  // tenant_users cannot access admin routes
  const adminRoutes = ['/tenant/add', '/users'];
  if (adminRoutes.some(route => location.pathname.startsWith(route))) {
    return <Navigate to="/" replace />;
  }
  
  // If we're on a tenant route, check if user has access to that tenant
  if (tenantId) {
    const hasAccess = authUser.tenant_roles?.some(tr => tr.tenant_id === tenantId);
    
    if (!hasAccess) {
      return (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              Access Denied
            </Typography>
            <Typography>
              You do not have access to this tenant. Please contact your administrator if you believe this is an error.
            </Typography>
          </Alert>
        </Box>
      );
    }
  }
  
  return <>{children}</>;
};

export default TenantAccessGuard;

